import React, { useEffect, useState } from 'react';
import { Save, Check, Key, Shield, Download, Trash2, User, CreditCard, Palette, Crown, Type, Eye, Bell } from 'lucide-react';
import { accountApi, privacyApi, subscriptionsApi, setAccessToken } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme, THEMES } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import UpgradeModal from '../components/UpgradeModal';
import { Skeleton } from '../components/Skeleton';
import './Pages.css';

export default function MeinKonto() {
  const { theme, setTheme } = useTheme();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'profil' | 'passwort' | 'abo' | 'darstellung' | 'dsgvo'>('profil');
  const [readingAid, setReadingAid] = useState(() => localStorage.getItem('cd365_reading_aid') === '1');
  const [msgSound, setMsgSound] = useState(() => localStorage.getItem('cd365_msg_sound') !== '0');

  // Profile
  const [profile, setProfile] = useState({ first_name: '', last_name: '', phone: '', title: '', email: '', role: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Password
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);

  // Subscription
  const [sub, setSub] = useState<any>(null);
  const [profileError, setProfileError] = useState(false);

  const loadProfile = () => {
    setProfileError(false);
    return accountApi.profile().then(res => {
      if (res.data?.email) {
        setProfile(res.data);
      } else {
        setProfileError(true);
      }
    }).catch(() => {
      setProfileError(true);
    });
  };

  useEffect(() => {
    Promise.all([
      loadProfile(),
      accountApi.subscription().then(res => setSub(res.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await accountApi.updateProfile({
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone || undefined,
        title: profile.title || undefined,
      });
      showToast('Profil gespeichert', 'success');
    } catch { showToast('Fehler beim Speichern', 'error'); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) {
      showToast('Passwörter stimmen nicht überein', 'error');
      return;
    }
    setPwSaving(true);
    try {
      await accountApi.changePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password });
      showToast('Passwort erfolgreich geändert', 'success');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Ändern', 'error');
    } finally { setPwSaving(false); }
  };

  return (
    <div>
      <div className="mb-24">
        <h1>Mein Konto</h1>
        <p className="text-muted text-sm">Persönliche Daten, Passwort und Abo-Status</p>
      </div>

      <div className="category-chips mb-24">
        <button className={`chip ${activeTab === 'profil' ? 'chip-active' : ''}`} onClick={() => setActiveTab('profil')}>
          <User size={14} style={{ marginRight: 4 }} /> Profil
        </button>
        <button className={`chip ${activeTab === 'passwort' ? 'chip-active' : ''}`} onClick={() => setActiveTab('passwort')}>
          <Key size={14} style={{ marginRight: 4 }} /> Passwort
        </button>
        <button className={`chip ${activeTab === 'abo' ? 'chip-active' : ''}`} onClick={() => setActiveTab('abo')}>
          <CreditCard size={14} style={{ marginRight: 4 }} /> Abo-Status
        </button>
        <button className={`chip ${activeTab === 'darstellung' ? 'chip-active' : ''}`} onClick={() => setActiveTab('darstellung')}>
          <Palette size={14} style={{ marginRight: 4 }} /> Darstellung
        </button>
        <button className={`chip ${activeTab === 'dsgvo' ? 'chip-active' : ''}`} onClick={() => setActiveTab('dsgvo')}>
          <Shield size={14} style={{ marginRight: 4 }} /> Datenschutz
        </button>
      </div>

      {/* Skeleton */}
      {loading && (activeTab === 'profil' || activeTab === 'abo') && (
        <div className="card" style={{ maxWidth: 720, padding: 32 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <Skeleton width="40%" height={14} />
            <div style={{ display: 'flex', gap: 16 }}>
              <Skeleton height={38} />
              <Skeleton height={38} />
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <Skeleton height={38} />
              <Skeleton height={38} />
            </div>
            <Skeleton width={180} height={40} borderRadius={8} />
          </div>
        </div>
      )}

      {/* Profil */}
      {activeTab === 'profil' && !loading && profileError && (
        <div className="card" style={{ maxWidth: 720, padding: 32, textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>Profildaten konnten nicht geladen werden.</p>
          <button type="button" className="btn btn-primary" onClick={() => { setLoading(true); loadProfile().finally(() => setLoading(false)); }}>
            Erneut laden
          </button>
        </div>
      )}
      {activeTab === 'profil' && !loading && !profileError && (
        <form onSubmit={handleProfileSave}>
          <div className="card" style={{ maxWidth: 720, padding: 32 }}>
            <div className="settings-section">
              <h3>Persönliche Daten</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Vorname</label>
                  <input value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Nachname</label>
                  <input value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Berufsbezeichnung</label>
                  <input value={profile.title} onChange={(e) => setProfile({ ...profile, title: e.target.value })} placeholder="z.B. Systemischer Coach" />
                </div>
                <div className="form-group">
                  <label>Telefon</label>
                  <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+49 ..." />
                </div>
              </div>
              <div className="form-group">
                <label>E-Mail-Adresse</label>
                <input value={profile.email} disabled style={{ background: 'var(--bg)', color: 'var(--text-muted)' }} />
                <p className="text-muted text-sm" style={{ marginTop: 4 }}>Die E-Mail-Adresse kann nicht geändert werden.</p>
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Save size={18} /> {saving ? 'Speichern...' : 'Änderungen speichern'}
            </button>
          </div>
        </form>
      )}

      {/* Passwort */}
      {activeTab === 'passwort' && (
        <form onSubmit={handlePasswordChange}>
          <div className="card" style={{ maxWidth: 720, padding: 32 }}>
            <div className="settings-section">
              <h3>Passwort ändern</h3>
              <div className="form-group">
                <label>Aktuelles Passwort</label>
                <input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Neues Passwort</label>
                <input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} placeholder="Mindestens 8 Zeichen" />
              </div>
              <div className="form-group">
                <label>Neues Passwort bestätigen</label>
                <input type="password" value={pwForm.confirm_password} onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })} />
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={pwSaving || !pwForm.current_password || !pwForm.new_password}>
              <Key size={18} /> {pwSaving ? 'Ändere...' : 'Passwort ändern'}
            </button>
          </div>
        </form>
      )}

      {/* Abo-Status */}
      {activeTab === 'abo' && !loading && (
        <AboTab sub={sub} />
      )}

      {/* Darstellung / Theme */}
      {activeTab === 'darstellung' && (
        <div className="card" style={{ maxWidth: 720, padding: 32 }}>
          <div className="settings-section">
            <h3>Farbschema</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 20 }}>Wähle dein bevorzugtes Farbschema. Die Änderung wird sofort angewendet.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    setTheme(t.id);
                    accountApi.updateTheme(t.id).catch(() => {
                      showToast('Theme konnte nicht gespeichert werden', 'error');
                    });
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    border: theme === t.id ? `2px solid ${t.primary}` : '2px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    background: 'var(--bg-card)',
                    transition: 'all 0.15s',
                    boxShadow: theme === t.id ? `0 0 0 2px ${t.primary}33` : 'none',
                  }}
                >
                  {/* Mini preview */}
                  <div style={{ display: 'flex', height: 60 }}>
                    <div style={{ width: 48, background: t.sidebar, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8, gap: 4 }}>
                      {[1, 2, 3].map((i) => (
                        <div key={i} style={{ width: 24, height: 3, borderRadius: 2, background: theme === t.id && i === 1 ? t.primary : 'rgba(255,255,255,0.25)' }} />
                      ))}
                    </div>
                    <div style={{ flex: 1, background: t.id === 'prestige-navy' ? '#0F172A' : '#F8FAFC', padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ width: '60%', height: 4, borderRadius: 2, background: t.primary }} />
                      <div style={{ width: '80%', height: 3, borderRadius: 2, background: t.id === 'prestige-navy' ? '#334155' : '#E2E8F0' }} />
                      <div style={{ width: '50%', height: 3, borderRadius: 2, background: t.id === 'prestige-navy' ? '#334155' : '#E2E8F0' }} />
                    </div>
                  </div>
                  {/* Label */}
                  <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid var(--border-light)' }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: t.primary, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.82rem', fontWeight: theme === t.id ? 600 : 400, color: 'var(--text)' }}>{t.name}</span>
                    {theme === t.id && <Check size={14} style={{ marginLeft: 'auto', color: t.primary }} />}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Lesehilfe */}
          <div className="settings-section" style={{ marginTop: 32 }}>
            <h3><Eye size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Lesehilfe</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 16 }}>Vergrößert die Schrift für bessere Lesbarkeit.</p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={readingAid}
                onChange={(e) => {
                  const on = e.target.checked;
                  setReadingAid(on);
                  document.documentElement.classList.toggle('reading-aid', on);
                  localStorage.setItem('cd365_reading_aid', on ? '1' : '');
                }}
                style={{ width: 20, height: 20, accentColor: 'var(--primary)' }}
              />
              <span style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
                <Type size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Größere Schrift aktivieren
              </span>
            </label>
          </div>

          {/* Benachrichtigungston */}
          <div className="settings-section" style={{ marginTop: 32 }}>
            <h3><Bell size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Benachrichtigungen</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 16 }}>Akustische Benachrichtigung bei neuen Nachrichten.</p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={msgSound}
                onChange={(e) => {
                  const on = e.target.checked;
                  setMsgSound(on);
                  localStorage.setItem('cd365_msg_sound', on ? '1' : '0');
                }}
                style={{ width: 20, height: 20, accentColor: 'var(--primary)' }}
              />
              <span style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
                <Bell size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Ton bei neuen Nachrichten
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Datenschutz / DSGVO */}
      {activeTab === 'dsgvo' && (
        <div className="card" style={{ maxWidth: 720, padding: 32 }}>
          <div className="settings-section">
            <h3><Shield size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Datenschutz (DSGVO)</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 16 }}>Verwalte deine Daten gemäß der Datenschutz-Grundverordnung.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'var(--bg)', borderRadius: 8 }}>
                <div>
                  <strong style={{ fontSize: '0.9rem' }}>Meine Daten exportieren</strong>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>Alle deine Daten als PDF herunterladen (Art. 20 DSGVO)</p>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={async () => {
                  try {
                    const res = await privacyApi.exportCoachData();
                    const blob = new Blob([res.data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url;
                    a.download = `CoachDesk365_Datenexport_${new Date().toISOString().split('T')[0]}.pdf`;
                    a.click(); URL.revokeObjectURL(url);
                  } catch { showToast('Export fehlgeschlagen', 'error'); }
                }}><Download size={14} /> Exportieren</button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'var(--danger)11', borderRadius: 8 }}>
                <div>
                  <strong style={{ fontSize: '0.9rem', color: '#991b1b' }}>Konto löschen</strong>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>Alle Daten unwiderruflich löschen (Art. 17 DSGVO)</p>
                </div>
                <button className="btn btn-danger btn-sm" onClick={async () => {
                  if (!window.confirm('Bist du sicher? Diese Aktion kann nicht rückgängig gemacht werden!')) return;
                  if (!window.confirm('LETZTE WARNUNG: Alle Daten werden gelöscht. Fortfahren?')) return;
                  try { await privacyApi.deleteAccount(); setAccessToken(null); window.location.href = '/login'; }
                  catch { showToast('Fehler beim Löschen', 'error'); }
                }}><Trash2 size={14} /> Konto löschen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Abo Tab ---
function AboTab({ sub }: { sub: any }) {
  const { subscription, refreshSubscription } = useAuth();
  const { showToast } = useToast();
  const [usage, setUsage] = useState<any>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    subscriptionsApi.usage().then(r => setUsage(r.data)).catch(() => {});
  }, []);

  const handleCancel = async () => {
    if (!window.confirm('Möchten Sie Ihr Abo wirklich kündigen? Sie verlieren den Zugang zu Premium-Features.')) return;
    setCancelling(true);
    try {
      await subscriptionsApi.cancel();
      showToast('Ihr Abo wurde gekündigt', 'success');
      refreshSubscription();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Kündigen', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const plan = subscription?.plan_type || sub?.plan_type || 'free';
  const status = subscription?.status || sub?.status || 'trial';
  const trialDays = subscription?.trial_days_remaining;
  const trialEndsAt = subscription?.trial_ends_at || sub?.trial_ends_at;

  const planLabels: Record<string, string> = { free: 'Free Trial', basic: 'Einzelcoach', team_s: 'Professional S', team_m: 'Professional M', enterprise: 'Organisation' };
  const statusLabels: Record<string, string> = { active: 'Aktiv', trial: 'Testphase', suspended: 'Pausiert', cancelled: 'Gekündigt', expired: 'Abgelaufen' };

  const features = [
    { name: 'Kalender & Buchung', plans: ['free', 'basic', 'team_s', 'team_m', 'enterprise'] },
    { name: 'Rechnungen & Angebote', plans: ['free', 'basic', 'team_s', 'team_m', 'enterprise'] },
    { name: 'Coaching-Pakete', plans: ['free', 'basic', 'team_s', 'team_m', 'enterprise'] },
    { name: 'Vorlagen & Protokolle', plans: ['free', 'basic', 'team_s', 'team_m', 'enterprise'] },
    { name: 'Google Drive', plans: ['free', 'basic', 'team_s', 'team_m', 'enterprise'] },
    { name: 'Team-Verwaltung', plans: ['team_s', 'team_m', 'enterprise'] },
    { name: 'Rollen & Rechte', plans: ['team_s', 'team_m', 'enterprise'] },
    { name: 'API-Zugang', plans: ['team_m', 'enterprise'] },
    { name: 'Webhooks & Zapier', plans: ['team_m', 'enterprise'] },
    { name: 'Eigenes Branding', plans: ['team_m', 'enterprise'] },
    { name: 'SSO / SAML', plans: ['enterprise'] },
  ];

  const hasPlan = (featurePlans: string[], p: string) => featurePlans.includes(p);

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Current Plan + Status */}
      <div className="card" style={{ padding: 32, marginBottom: 20 }}>
        <div className="flex-between" style={{ alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ margin: '0 0 4px' }}>Mein Abo</h3>
            <p className="text-muted text-sm" style={{ margin: 0 }}>Verwalte deinen Plan und deine Nutzung</p>
          </div>
          {status === 'trial' && trialDays != null && (
            <span className={`abo-trial-badge ${trialDays <= 7 ? 'abo-trial-badge-urgent' : 'abo-trial-badge-ok'}`}>
              Free Trial — endet in {trialDays} Tagen
            </span>
          )}
        </div>

        <div className="abo-plan-grid">
          <div className="abo-plan-box">
            <p className="text-muted text-sm" style={{ margin: 0 }}>Aktueller Plan</p>
            <p style={{ fontSize: '1.2rem', fontWeight: 700, margin: '4px 0 0', color: 'var(--primary)' }}>
              {planLabels[plan] || plan}
            </p>
          </div>
          <div className="abo-plan-box">
            <p className="text-muted text-sm" style={{ margin: 0 }}>Status</p>
            <p style={{
              fontSize: '1.1rem', fontWeight: 600, margin: '4px 0 0',
              color: status === 'active' ? 'var(--success)' : status === 'trial' ? 'var(--warning)' : 'var(--danger)',
            }}>
              {statusLabels[status] || status}
            </p>
          </div>
        </div>

        {/* Usage Bars */}
        {usage && (
          <div className="abo-usage-grid">
            <div>
              <div className="flex-between" style={{ marginBottom: 4 }}>
                <span className="text-sm">Klienten</span>
                <span className="text-sm text-muted">
                  {usage.clients_count} / {usage.clients_limit ?? '\u221E'}
                </span>
              </div>
              <div className="abo-progress-bar">
                <div
                  className="abo-progress-fill"
                  style={{
                    width: usage.clients_limit ? `${Math.min(100, (usage.clients_count / usage.clients_limit) * 100)}%` : '30%',
                  }}
                />
              </div>
            </div>
            <div>
              <div className="flex-between" style={{ marginBottom: 4 }}>
                <span className="text-sm">Sitzungen (Monat)</span>
                <span className="text-sm text-muted">
                  {usage.sessions_this_month} / {usage.sessions_limit ?? '\u221E'}
                </span>
              </div>
              <div className="abo-progress-bar">
                <div
                  className="abo-progress-fill"
                  style={{
                    width: usage.sessions_limit ? `${Math.min(100, (usage.sessions_this_month / usage.sessions_limit) * 100)}%` : '25%',
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trial Progress */}
      {status === 'trial' && trialEndsAt && (
        <div className="card" style={{ padding: 24, marginBottom: 20 }}>
          <div className="flex-between">
            <div>
              <strong>14-Tage Free-Trial aktiv</strong>
              <p className="text-muted text-sm" style={{ margin: '2px 0 0' }}>
                Endet am {new Date(trialEndsAt).toLocaleDateString('de-DE')} — danach Plan wählen oder Konto deaktiviert
              </p>
            </div>
            <button className="btn btn-primary" onClick={() => setShowUpgrade(true)}>
              <Crown size={16} style={{ marginRight: 4 }} /> Jetzt upgraden
            </button>
          </div>
          {trialDays != null && (
            <>
              <div className="abo-progress-bar" style={{ marginTop: 12 }}>
                <div
                  className={`abo-progress-fill ${trialDays <= 7 ? 'abo-progress-fill-danger' : 'abo-progress-fill-warning'}`}
                  style={{ width: `${Math.min(100, ((14 - trialDays) / 14) * 100)}%` }}
                />
              </div>
              <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                {14 - trialDays} von 14 Tagen verbraucht
              </p>
            </>
          )}
        </div>
      )}

      {/* Feature Access */}
      <div className="card" style={{ padding: 24, marginBottom: 20 }}>
        <h4 style={{ margin: '0 0 12px' }}>Feature-Zugang</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {features.map((f) => {
            const hasAccess = hasPlan(f.plans, plan);
            const minPlan = f.plans[0];
            return (
              <span key={f.name} className={`feature-tag ${hasAccess ? 'feature-tag-active' : 'feature-tag-locked'}`}>
                {hasAccess ? '\u2713' : '\uD83D\uDD12'} {f.name}
                {!hasAccess && <span style={{ fontSize: '0.7rem', marginLeft: 4 }}>(ab {planLabels[minPlan]})</span>}
              </span>
            );
          })}
        </div>
      </div>

      {/* Plan wechseln + Kuendigen for active users */}
      {status === 'active' && (
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          {plan !== 'enterprise' && (
            <button className="btn btn-primary" onClick={() => setShowUpgrade(true)}>
              <Crown size={16} style={{ marginRight: 4 }} /> Plan wechseln
            </button>
          )}
          <button
            className="btn btn-sm"
            style={{ color: 'var(--danger)', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.85rem' }}
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? 'Wird gekündigt...' : 'Abo kündigen'}
          </button>
        </div>
      )}

      {showUpgrade && (
        <UpgradeModal
          currentPlan={plan}
          onClose={() => setShowUpgrade(false)}
          onUpgraded={() => refreshSubscription()}
        />
      )}
    </div>
  );
}
